import { User } from "@prisma/client";
import ProfessionalJourneyProject from "immigram-ui-b2c/src/components/ProfessionalJourneyCompany/ProfessionalJourneyProject";
import Badge, { BadgeType } from "immigram-ui-cmon/src/components/Badge/Badge";
import Button, {
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import {
  Icon24DocUpload,
  Icon24Plus,
  Icon24Trash,
} from "immigram-ui-cmon/src/components/Icons";
import Paragraph from "immigram-ui-cmon/src/components/Paragraph/Paragraph";
import { useAutoScrollToFieldWithError } from "immigram-ui-cmon/src/hooks/useAutoScrollToFieldWithError";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import * as React from "react";
import { useCallback } from "react";

import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { isAdmin, isDebug } from "@/lib/auth/rbac";
import { AppPages, getApplicationRelLink } from "@/lib/pages";
import { ApplicationFull } from "@/lib/prisma";

import { DashedBlock } from "@/components/DashedBlock/DashedBlock";

import ProfessionalJourneyMenu from "./ProfessionalJourneyMenu";

interface ProfessionalJourneyCompanyProps<CompanyEventData, ProjectEventData> {
  id: string;
  companyData: CompanyEventData;
  companyName?: string | null;
  yearFrom?: string | null;
  yearTo?: string | null;
  position?: string | null;
  checkbox?: boolean;
  projects?: {
    id?: string;
    name?: string | null;
    projectData: ProjectEventData;
    status?: ApplicationSectionStatusType | null;
    hasUnresolvedComments?: boolean;
    isValid?: boolean | null;
  }[];
  editable?: boolean;
  sectionStatus?: ApplicationSectionStatusType;
  showErrors?: boolean;
  onClick?: ({ companyData }: { companyData: CompanyEventData }) => void;
  onDelete?: ({ companyData }: { companyData: CompanyEventData }) => void;
  onProjectCreate?: ({
    companyData,
  }: {
    companyData: CompanyEventData;
  }) => void;
  onProjectClick?: ({
    projectData,
    companyData,
  }: {
    projectData: ProjectEventData;
    companyData: CompanyEventData;
  }) => void;
  onProjectCheckboxChange?: ({
    checked,
    projectData,
    companyData,
  }: {
    checked: boolean;
    projectData: ProjectEventData;
    companyData: CompanyEventData;
  }) => void;
  ownUser: User;
  application: ApplicationFull;
  showProvideDocsButton?: boolean | null;
  onGenerateDoc?: (companyData: CompanyEventData) => Promise<void>;
  onGenerateChecklist?: (companyData: CompanyEventData) => Promise<void>;
}

export default function ProfessionalJourneyCompany<
  CompanyEventData,
  ProjectEventData
>({
  id,
  companyData,
  companyName,
  yearFrom,
  yearTo,
  position,
  projects,
  checkbox,
  editable,
  sectionStatus,
  application,
  showErrors,
  showProvideDocsButton,
  onClick,
  onDelete,
  onProjectCreate,
  onProjectClick,
  onProjectCheckboxChange,
  onGenerateDoc,
  onGenerateChecklist,
  ownUser,
}: ProfessionalJourneyCompanyProps<CompanyEventData, ProjectEventData>) {
  const { t } = useTranslation();
  const elementRef = React.useRef<HTMLDivElement>(null);

  const handleClick = useCallback(() => {
    onClick?.({ companyData });
  }, [onClick, companyData]);

  const handleDelete = useCallback(
    (e?: React.MouseEvent<Element>) => {
      e?.stopPropagation();
      onDelete?.({ companyData });
    },
    [onDelete, companyData]
  );

  const handleCreateProject = useCallback(
    (e: React.MouseEvent<Element>) => {
      e.stopPropagation();
      onProjectCreate?.({ companyData });
    },
    [onProjectCreate, companyData]
  );

  const handleProjectClick = useCallback(
    ({ projectData }: { projectData: ProjectEventData }) => {
      onProjectClick?.({ companyData, projectData });
    },
    [onProjectClick, companyData]
  );

  const handleProjectCheckboxChange = useCallback(
    ({
      projectData,
      checked,
    }: {
      projectData: ProjectEventData;
      checked: boolean;
    }) => {
      onProjectCheckboxChange?.({ checked, projectData, companyData });
    },
    [onProjectCheckboxChange, companyData]
  );

  const handleGenerateChecklist = useCallback(
    () => onGenerateChecklist?.(companyData),
    [onGenerateChecklist, companyData]
  );

  const handleGenerateDoc = useCallback(
    () => onGenerateDoc?.(companyData),
    [onGenerateDoc, companyData]
  );

  useAutoScrollToFieldWithError(
    `prof-journey.${id}`,
    elementRef.current,
    !!showErrors
  );

  return (
    <div
      className="group cursor-pointer rounded-[20px] border border-gray-150 pl-8t pr-6 pt-5 pb-8t hover:shadow-sm"
      onClick={handleClick}
      ref={elementRef}
    >
      <div className="m-0 mb-7 flex items-baseline justify-between border-b border-gray-150 pb-4">
        <div className="flex">
          <Paragraph className="mb-0 overflow-hidden text-ellipsis font-granate text-2xl text-black-900">
            {companyName ||
              t("application:professionalJourneyCompany.companyName.default")}
          </Paragraph>
          {yearFrom && yearTo ? (
            <Paragraph className="my-0 mr-[60px] ml-2 flex h-6 translate-y-3t items-center justify-center whitespace-nowrap rounded-[8px] bg-gray-100 px-2 py-2t font-granate text-smb text-black-900 backdrop-blur-[16px] ">
              {[yearFrom, yearTo].join(" - ")}
            </Paragraph>
          ) : null}
        </div>
        {isAdmin(ownUser) ? (
          <ProfessionalJourneyMenu
            onDelete={editable ? handleDelete : undefined}
            onGenerateChecklist={handleGenerateChecklist}
            onGenerateDoc={handleGenerateDoc}
          />
        ) : (
          <>
            {editable && (
              <Button
                onClick={handleDelete}
                className="invisible group-hover:visible"
                icon={<Icon24Trash />}
                type={ButtonType.Secondary}
                size="200"
              />
            )}
          </>
        )}
      </div>
      {position ? (
        <Paragraph className="m-0 mb-2 p-0 text-mdb text-black-600">
          {position}
        </Paragraph>
      ) : null}
      <div className="flex flex-col gap-5">
        {projects?.length ? (
          <div className={clsxm("grid grid-cols-2 gap-x-3 gap-y-2")}>
            {projects.map((project) => (
              <ProfessionalJourneyProject<ProjectEventData>
                id={project.id}
                ownUser={ownUser}
                key={project.id}
                name={project.name}
                isValid={project.isValid}
                showErrors={showErrors}
                status={project.status}
                hasUnresolvedComments={project.hasUnresolvedComments}
                projectData={project.projectData}
                checkbox={checkbox}
                sectionStatus={sectionStatus}
                onClick={handleProjectClick}
                onCheckboxChange={handleProjectCheckboxChange}
              />
            ))}
          </div>
        ) : null}
        {showProvideDocsButton && (
          <Button
            href={getApplicationRelLink({
              applicationId: application.id,
              link: `${AppPages.ApplicationProfileSectionDoc}#prof-journey-${id}`,
            })}
            onClick={(e) => e.stopPropagation()}
            className="self-start"
            type={ButtonType.Secondary}
            icon={<Icon24DocUpload className="ml-1 flex h-5 w-4" />}
            size="200"
          >
            {t("application:professionalJourneyCompany.provideDocuments")}
          </Button>
        )}
        {editable && (
          <DashedBlock
            className="flex h-14 cursor-pointer items-center justify-center rounded-2xl font-semibold text-black-900"
            onClick={handleCreateProject}
          >
            <div className="flex h-full items-center justify-center gap-1">
              <Icon24Plus />
              {t("application:professionalJourneyCompany.addProject")}
            </div>
          </DashedBlock>
        )}
        {isDebug(ownUser) && (
          <Badge className="self-start" size="100" type={BadgeType.Neutral}>
            {id}
          </Badge>
        )}
      </div>
    </div>
  );
}
